import { useState } from "react";
import { useBookings } from "../lib/tenant-api";
import { formatDateTime } from "../lib/format";
import {
  EmptyState,
  ErrorBanner,
  LoadingRow,
} from "../components/EmptyState";

type Filter = "upcoming" | "past" | "all";

export default function Bookings() {
  const [filter, setFilter] = useState<Filter>("upcoming");
  const { data, isLoading, error } = useBookings(filter);
  return (
    <div>
      <div className="mb-4 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bookings</h1>
          <p className="mt-1 text-sm text-slate-500">
            Appointments confirmed by the receptionist. Google Calendar events
            (when connected) sync automatically.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          {(["upcoming", "past", "all"] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-sm rounded-md ${
                filter === f
                  ? "bg-brand-100 text-brand-700 font-medium"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>
      {isLoading && <LoadingRow />}
      {error && <ErrorBanner message={(error as Error).message} />}
      {data && data.length === 0 && (
        <EmptyState
          icon="📅"
          title="No bookings"
          hint={
            filter === "upcoming"
              ? "Nothing scheduled. New bookings appear here automatically."
              : "No bookings match this filter."
          }
        />
      )}
      {data && data.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left font-medium px-4 py-2">When</th>
                <th className="text-left font-medium px-4 py-2">Customer</th>
                <th className="text-left font-medium px-4 py-2">Contact</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
                <th className="text-left font-medium px-4 py-2">Source</th>
              </tr>
            </thead>
            <tbody>
              {data.map((b) => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-slate-900">
                    {formatDateTime(b.start_time)}
                    {b.notes && (
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                        {b.notes}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {b.customer_name}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-700">{b.customer_phone || "—"}</div>
                    {b.customer_email && (
                      <div className="text-xs text-slate-500">
                        {b.customer_email}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill status={b.status} />
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {b.provider === "google_calendar"
                      ? "Google Calendar"
                      : "local only"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const map: Record<string, string> = {
    confirmed: "bg-emerald-100 text-emerald-700",
    tentative: "bg-amber-100 text-amber-700",
    cancelled: "bg-slate-200 text-slate-500 line-through",
  };
  return (
    <span
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
        map[status] ?? "bg-slate-100 text-slate-600"
      }`}
    >
      {status}
    </span>
  );
}
